import { recorderPausedAtom } from '~/atoms/recorder-atoms';

import { api } from '~/trpc-extra/api';
import { useAtomValue } from 'jotai';
import { useLiveStateManager } from '~/hooks/useLiveStateManager';

export default function RecorderStatusDisplay({
  testContentId,
  isCancellingExecution,
}: {
  testContentId: string;
  isCancellingExecution: boolean;
}) {
  const { data: testContent, isLoading: testContentLoading } = api.testContent.get.useQuery(
    { id: testContentId },
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled: !!testContentId },
  );
  const { vmStatus, recorderState } = useLiveStateManager();
  const recorderPaused = useAtomValue(recorderPausedAtom);

  if (isCancellingExecution) {
    return <div className="font-bold">Cancelling Execution...</div>;
  }

  if (testContentLoading) {
    return <div>Loading...</div>;
  }

  const getExecutionStepIndex = () => {
    const steps = testContent?.steps;
    if (!steps?.length) {
      return -1;
    }

    const currentStepId = vmStatus?.executionState?.currentStepId;
    if (currentStepId) {
      const currentStepIdx = steps.findIndex((step) => step.id === currentStepId);
      if (currentStepIdx !== -1) {
        return currentStepIdx;
      }
    }

    const executionOperationId = vmStatus?.executionState?.operationId;
    if (!executionOperationId) {
      return -1;
    }

    const aiStepIdx = steps.findIndex((step) => step.id === executionOperationId);
    if (aiStepIdx !== -1) {
      return aiStepIdx;
    }

    return steps.findIndex((step) => step.operations.some((op) => op.id === executionOperationId));
  };

  if (recorderState?.executionType === 'preview') {
    if (vmStatus?.state === 'error') {
      return <div>Preview error</div>;
    }

    if (vmStatus?.state !== 'replaying') {
      return <div>Preview loading...</div>;
    }

    const stepIdx = getExecutionStepIndex();
    return stepIdx !== -1 ?
        <span className="font-bold">Executing at Step {stepIdx + 1}</span>
      : <span>Replaying...</span>;
  }

  if (recorderState?.executionType === 'record') {
    if (vmStatus?.state === 'replaying') {
      const stepIdx = getExecutionStepIndex();
      const step = stepIdx !== -1 ? testContent?.steps[stepIdx] : undefined;
      return step != null && stepIdx !== -1 ?
          <span className="font-bold">Replaying at Step {stepIdx + 1}</span>
        : <span>Replaying...</span>;
    } else if (
      vmStatus?.state === 'record-action' ||
      vmStatus?.state === 'select-options' ||
      vmStatus?.state === 'fill-input' ||
      vmStatus?.state === 'ready'
    ) {
      const stepIndex = testContent?.steps.findIndex((step) => step.id === recorderState?.stepId);
      const step = stepIndex !== undefined ? testContent?.steps[stepIndex] : null;
      if (recorderPaused) {
        return (
          <div className="py-1 font-bold text-amber-600 dark:text-amber-400">VM is on but recording is paused</div>
        );
      }
      return step && stepIndex !== undefined && stepIndex !== -1 ?
          <span className="font-bold">Recording at Step {stepIndex + 1}</span>
        : <span>Step not found</span>;
    } else {
      return <div>Recorder loading...</div>;
    }
  }

  return <div>Loading...</div>;
}
